import { signal, Signal } from '@preact/signals-react'

export enum LogLevel {
    DEBUG = 'debug',
    INFO = 'info',
    WARN = 'warn',
    ERROR = 'error',
}

export interface LogEntry {
    id: string
    timestamp: Date
    level: LogLevel
    message: string
    source?: string
    error?: Error | string
    stack?: string
    context?: Record<string, unknown>
}

export class Logger {
    private logs: Signal<LogEntry[]> = signal([])
    static MAX_LOGS: number = 200
    private static instance: Logger | null = null

    static getInstance(): Logger {
        if (!Logger.instance) {
            Logger.instance = new Logger()
        }
        return Logger.instance
    }

    getLogs(): LogEntry[] {
        return this.logs.value
    }

    log(
        level: LogLevel,
        message: string,
        options?: {
            source?: string
            error?: Error | string
            context?: Record<string, unknown>
        }
    ): void {
        const entry: LogEntry = {
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            timestamp: new Date(),
            level,
            message,
            source: options?.source,
            error: options?.error,
            stack: options?.error instanceof Error ? options.error.stack : undefined,
            context: options?.context,
        }

        const currentLogs = this.logs.value
        const newLogs = [entry, ...currentLogs].slice(0, Logger.MAX_LOGS)
        this.logs.value = newLogs

        const consoleMethod =
            {
                [LogLevel.DEBUG]: console.debug,
                [LogLevel.INFO]: console.info,
                [LogLevel.WARN]: console.warn,
                [LogLevel.ERROR]: console.error,
            }[level] || console.log

        const prefix = options?.source ? `[${options.source}]` : '[Logger]'
        consoleMethod(`${prefix} ${message}`, options?.error || '', options?.context || '')
    }

    debug(message: string, options?: { source?: string; context?: Record<string, unknown> }): void {
        this.log(LogLevel.DEBUG, message, options)
    }

    info(message: string, options?: { source?: string; context?: Record<string, unknown> }): void {
        this.log(LogLevel.INFO, message, options)
    }

    warn(
        message: string,
        options?: { source?: string; error?: Error | string; context?: Record<string, unknown> }
    ): void {
        this.log(LogLevel.WARN, message, options)
    }

    error(
        message: string,
        options?: { source?: string; error?: Error | string; context?: Record<string, unknown> }
    ): void {
        this.log(LogLevel.ERROR, message, options)
    }

    logPythonError(
        message: string,
        error: unknown,
        options?: {
            source?: string
            pythonCode?: string
            context?: Record<string, unknown>
        }
    ): void {
        const errorMessage = error instanceof Error ? error.message : String(error)
        const errorString = typeof error === 'string' ? error : errorMessage

        this.error(message, {
            source: options?.source || 'PythonCode',
            error: errorString,
            context: {
                ...options?.context,
                pythonCode: options?.pythonCode,
            },
        })
    }

    clear(): void {
        this.logs.value = []
    }
}
